import { APIResponse } from "./types";
import _ from "lodash";
import { DatabaseManager } from "helpers/db";
import { handleError } from "helpers/error_handler";
import { executeLambda } from "helpers/lambda";
import { publishToSns } from "helpers/sns_push";
import version_map from "../config/version_map";
import {
  ENTITY_TYPES,
  PROCUREMENT_STAGES,
  PROCUREMENT_STAGES_KEY,
  PROCUREMENT_STATUS,
  PROCUREMENT_TYPES,
  buildStageSK,
} from "../types";
import moment from "moment";
import { getUserIdentity } from "../helpers/auth";

const dbManager = new DatabaseManager();

/**
 * @deprecated NOT to be used
 * @param formData
 */
const initializeProcurementApi: (formData: {
  sizingInfo: any;
  procurement_name: string;
  procurement_type: string;
  estimated_tshirt_size: string;
  cluster_name: string;
  estimated_price: number;
  additional_nodes: number;
  PK: string;
  SK: string;
}) => Promise<APIResponse> = async (formData) => {
  try {
    //TODO:
    let procurement_info = {
      PK: formData.PK,
      SK: formData.SK,
      entity_type: ENTITY_TYPES.Procurement,
      procurement_name: formData.procurement_name,
      procurement_type: formData.procurement_type,
      procurement_status: PROCUREMENT_STATUS.Initial,
    };

    let stage_sizing = {
      PK: formData.PK,
      SK: buildStageSK(PROCUREMENT_STAGES.Sizing),
      entity_type: ENTITY_TYPES.ProcurementStage,
      stage_name: PROCUREMENT_STAGES.Sizing,
      stage_status: PROCUREMENT_STATUS.Initial,

      region: "",
      country: "",
      site_code: "",
      site_name: "",
      cluster_name: formData.cluster_name,
      cluster_info: {},
      expected_growth_rate: formData.sizingInfo.expected_growth_rate,
      existing: formData.sizingInfo.existing,
      buffer: formData.sizingInfo.buffer,
      subtotal: formData.sizingInfo.subtotal,
      grand_total: formData.sizingInfo.grand_total,
      additional_nodes: formData.additional_nodes,

      estimated_tshirt_size: formData.estimated_tshirt_size,
      estimated_price: formData.estimated_price,
    };

    if (formData.procurement_type === PROCUREMENT_TYPES.NEW) {
      let stage_sizing_new_proc = {
        projection_year_1: formData.sizingInfo.projection_year_1,
        projection_year_2: formData.sizingInfo.projection_year_2,
        projection_year_3: formData.sizingInfo.projection_year_3,
      };
      _.merge(stage_sizing, stage_sizing_new_proc);
    }
    // else if (formData.procurement_type === PROCUREMENT_TYPES.EXPANSION) {
    //   let stage_intake_expansion_proc = {
    //     "additional_req": formData.sizingInfo.additional_req
    //   }
    //   _.merge(stage_intake, stage_intake_expansion_proc)
    // }

    //TODO: Convert to BatchWrite
    await dbManager.createObject(
      process.env.REACT_APP_DYNAMO_DB_PROCUREMENTS || "",
      procurement_info
    );
    await dbManager.createObject(
      process.env.REACT_APP_DYNAMO_DB_PROCUREMENTS || "",
      stage_sizing
    );

    return {
      success: true,
      data: {},
      error: null,
      code: "",
    };
  } catch (e: any) {
    handleError(e);
    return {
      success: false,
      code: "500",
      data: null,
      error: e,
    };
  }
};

/**
 * Get Procurement details for given PK
 * @param PK
 * @param stageName
 */
const getProcurementDetails: (
  PK: string,
  stageName?: string | undefined
) => Promise<APIResponse> = async (PK, stageName?: string | undefined) => {
  try {
    let keyConditionExpression = "PK = :pk_value";
    let expressionAttributeValues = {};

    if (stageName) {
      keyConditionExpression += " AND SK = :sk_value";
      expressionAttributeValues = {
        ":pk_value": PK,
        ":sk_value": buildStageSK(stageName),
      };
    } else {
      expressionAttributeValues = {
        ":pk_value": PK,
      };
    }

    let response = await dbManager.queryTable(
      process.env.REACT_APP_DYNAMO_DB_PROCUREMENTS || "",
      keyConditionExpression,
      undefined,
      expressionAttributeValues
    );

    return {
      success: true,
      data: response.data,
      error: null,
      code: 200,
    };
  } catch (e: any) {
    console.log(e);
    handleError(e);
    return {
      success: false,
      code: "500",
      data: null,
      error: e,
    };
  }
};

/**
 * Get All Procurement Stage Items for
 */
const getAllProcurementStages: () => Promise<APIResponse> =
  async () => {
    try {
      // Updated filter to only check for entity_type and valid dates
      let filterExpression = 'entity_type = :entity_type_value ' +
        'and attribute_exists(created_at) '
      let expressionAttributeValues = {
        ':entity_type_value': ENTITY_TYPES.ProcurementStage
      }
      let response = await dbManager.getAllObjects(
        process.env.REACT_APP_DYNAMO_DB_PROCUREMENTS || "",
        filterExpression,
        undefined,
        expressionAttributeValues,
        "PK, SK, stage_name, stage_status, created_at, completed_at"
      )

    return {
      success: true,
      data: response.data,
      error: null,
      code: 200,
    };
  } catch (e: any) {
    handleError(e);
    return {
      success: false,
      code: "500",
      data: null,
      error: e,
    };
  }
};

/**
 * Updates the Procurement Stage Item
 * @param stageItem
 */
const updateProcurementStageItem: (
  stageItem: any
) => Promise<APIResponse> = async (stageItem) => {
  try {
    if (stageItem["stage_status"] === PROCUREMENT_STATUS.Submitted) {
      stageItem["assessed_at"] = moment.utc().toISOString();
      stageItem["assessed_by"] = (await getUserIdentity()).email;
    } else if (stageItem["stage_status"] === PROCUREMENT_STATUS.Approved) {
      stageItem["completed_at"] = moment.utc().toISOString();
      stageItem["completed_by"] = (await getUserIdentity()).email;
    }
    let response = await dbManager.updateObject(
      process.env.REACT_APP_DYNAMO_DB_PROCUREMENTS || "",
      stageItem
    );
    return {
      success: true,
      data: {},
      code: "",
      error: "",
    };
  } catch (e: any) {
    handleError(e);
    return {
      success: false,
      code: "500",
      data: null,
      error: e,
    };
  }
};

const updateSizingInfoAPI: (
  PK: string,
  SK: string,
  sizingInfo: {
    [key: string]: any;
  },
  version_number: number
) => Promise<APIResponse> = async (PK, SK, sizingInfo, version_number) => {
  try {
    let key = { PK: PK, SK: SK };
    let pathSegments = version_map[4]["SIZING_INFO_PATH"].split(".");
    let _pathSegments = pathSegments.map(
      (seg) => "#" + seg.replaceAll(/[ /]/g, "_")
    );
    let updateExpression = "";
    let expressionAttributeNames = {};
    let expressionAttributeValues = {};

    pathSegments.forEach((seg, idx) => {
      // @ts-ignore
      expressionAttributeNames[_pathSegments[idx]] = seg;
    });
    updateExpression = "SET " + _pathSegments.join(".") + " = :value";
    expressionAttributeValues = { ":value": sizingInfo };

    //TODO: Needs to be fixed
    let result = await dbManager.updateRecord(
      process.env.REACT_APP_DYNAMO_DB_PROCUREMENTS || "",
      key,
      updateExpression,
      undefined,
      expressionAttributeNames,
      expressionAttributeValues
    );
    return {
      success: true,
      data: {},
      code: "",
      error: "",
    };
  } catch (e: any) {
    handleError(e);
    return {
      success: false,
      code: "500",
      data: null,
      error: e,
    };
  }
};

//TODO: Not used?
// const determinedSizingInfoAPI: (
//   formData: {
//     PK: string,
//     SK: string,
//     tshirt_size: any
//   },
//   version_number: number,
//   operation_type: string
// ) => Promise<APIResponse> =
//   async (formData,
//          version_number,
//          operation_type = 'UPDATE') => {
//     try {
//       if (operation_type === 'UPDATE') {
//         let result = await dbManager.updateRecord(
//           process.env.REACT_APP_DYNAMO_DB_PROCUREMENTS || "",
//           'PK',
//           formData['Id'],
//           version_map[version_number]['DETERMINED_SIZING_INFO_PATH'],
//           {tshirt_size: formData.tshirt_size})
//         return {
//           success: result.success,
//           data: result.data,
//           code: '',
//           error: result.error
//         }
//       }
//       throw new Error("Unsupported operation_type provided in the react app")
//     } catch (e: any) {
//       handleError(e)
//       return {
//         success: false,
//         code: '500',
//         data: null,
//         error: e
//       }
//     }
//   }

const sendEmailAPI: (
  type: "initialize_procurement" | "custom",
  email_params: {
    html: string;
    subject: string;
    to: string[];
    cc: string[];
    bcc: string[];
    replymail: string[];
  }
) => Promise<APIResponse> = async (type, email_params) => {
  // try {
  //   let recipients_type = ['to', 'cc', 'bcc', 'replymail']
  //   let partial_email_params: any = {}
  //   recipients_type.forEach((rec) => {
  //     let param: string[] | boolean = _.get(email_params, rec, false)
  //     if (param && !_.isArray(param)) {
  //       throw new Error(`Parameter ${rec} should be an array`)
  //     }
  //     //@ts-ignore
  //     if (param && param?.length > 0) {
  //       //@ts-ignore
  //       partial_email_params[rec] = param.join(',')
  //     }
  //   })
  //   if (process.env.REACT_APP_SEND_EMAILS === 'true') {
  //     let result = await executeLambda(process.env.REACT_APP_LAMBDA_EMAIL || "", {
  //       body: {
  //         html: email_params['html'],
  //         subject: email_params['subject'],
  //         ...partial_email_params
  //       }
  //     })
  //     if (result.success && result.data?.['statusCode'] !== 204) {
  //       return {
  //         success: false,
  //         error: 'Error occured',
  //         data: null,
  //         code: '500'
  //       }
  //     }
  //   } else {
  //     console.debug('Not sending email. Data: ')
  //     console.debug(email_params)
  //   }
  //   return {
  //     success: true,
  //     error: null,
  //     data: null,
  //     code: '201'
  //   }

  // } catch (e) {
  //   return {
  //     success: false,
  //     error: e,
  //     data: null,
  //     code: '500'
  //   }
  // }
  try {
    let recipients_type = ["to", "cc", "bcc", "replymail"];
    let partial_email_params: any = {};

    recipients_type.forEach((rec) => {
      let param: string[] | boolean = _.get(email_params, rec, false);
      if (param && !_.isArray(param)) {
        throw new Error(`Parameter ${rec} should be an array`);
      }
      //@ts-ignore
      if (param && param?.length > 0) {
        //@ts-ignore
        partial_email_params[rec] = param.join(",");
      }
    });

    if (process.env.REACT_APP_SEND_EMAILS === "true") {
      let result = await publishToSns(
        process.env.REACT_APP_SNS_EVENTS_ARN || "",
        JSON.stringify({
          html: email_params["html"],
          subject: email_params["subject"],
          ...partial_email_params,
        }),
        {
          event_type: {
            DataType: "String",
            StringValue: "email_triggered", //can be made dynamic take from email_params..
          },
        }
      );

      if (!result.success) {
        return {
          success: false,
          error: "Error occurred while publishing to SNS",
          data: null,
          code: "500",
        };
      }
    } else {
      console.debug("Not sending email. Data: ", email_params);
    }

    return {
      success: true,
      error: null,
      data: null,
      code: "201",
    };
  } catch (e) {
    return {
      success: false,
      error: e,
      data: null,
      code: "500",
    };
  }
};

/**
 * Calls lambda - prism-v3-new-version
 * @param cluster_name
 * @param region
 */
const getClusterInfo: (
  cluster_name: string,
  region: string
) => Promise<APIResponse> = async (cluster_name, region) => {
  try {
    if (_.isEmpty(cluster_name)) {
      throw new Error("Cluster name cannot be empty");
    }
    let body: any = { cluster_name };
    if (region) {
      body["region"] = region;
    }
    let response = await executeLambda(
      process.env.REACT_APP_LAMBDA_PRISM_CLUSTER_INFO_API || "",
      body
    );

    if (response.success && response.data) {
      if (response.data?.statusCode !== 200) {
        return {
          success: false,
          error: JSON.parse(response.data.body),
          data: null,
          code: response.data.statusCode,
        };
      }

      return {
        success: true,
        error: null,
        data: response.data.body, //JSON.parse(response.data.body),
        code: 200,
      };
    }

    return {
      success: false,
      error: null,
      data: null,
      code: 500,
    };
  } catch (e) {
    return {
      success: false,
      error: e,
      data: null,
      code: 500,
    };
  }
};

const clusterNamesAPI: (search_q?: string) => Promise<APIResponse> = async (
  search_q = ""
) => {
  try {
    let result = await dbManager.getAllObjects(
      process.env.REACT_APP_DYNAMO_DB_CLUSTER_NAMES_TABLE_NAME || ""
    );
    if (!result.success) {
      throw result.error;
    }
    return {
      success: true,
      data: result.data,
      error: null,
      code: "",
    };
  } catch (e: any) {
    return {
      success: false,
      code: "500",
      data: null,
      error: e,
    };
  }
};

const existingClusterSizesAPI: () => Promise<APIResponse> = async () => {
  try {
    let keyConditionExpression = "PK = :pk_value";
    let expressionAttributeValues = {
      ":pk_value": "ExistingClusterSizing",
    };

    //@ts-ignore
    let result = await dbManager.queryTable(
      process.env.REACT_APP_DYNAMO_DB_SIZING || "",
      keyConditionExpression,
      undefined,
      expressionAttributeValues,
      undefined
    );

    if (!result.success) {
      throw result.error;
    }
    return {
      success: true,
      data: result.data,
      error: null,
      code: "",
    };
  } catch (e: any) {
    return {
      success: false,
      code: "500",
      data: null,
      error: e,
    };
  }
};

/**
 * Get T-Shirt Sizing for New Cluster
 * @param total_vcpu
 * @param total_ram
 * @param total_storage
 * @param total_nics
 */
const getTShirtSizingForNewCluster: (
  total_vcpu: string,
  total_ram: string,
  total_storage: string,
  total_nics?: string
) => Promise<APIResponse> = async (
  total_vcpu,
  total_ram,
  total_storage,
  total_nics
) => {
  try {
    let in_body = {
      vCPU: total_vcpu,
      RAM: total_ram,
      STORAGE: total_storage,
    };

    let response = await executeLambda(
      process.env.REACT_APP_LAMBDA_TSHIRT_SIZING_NEW_CLUSTER || "",
      in_body
    );
    //response format
    // {
    //   'Tshirt_size': "3 NODE",
    //   'price': 1234.56
    // }

    if (response.success && response.data) {
      if (response.data?.statusCode !== 200) {
        return {
          success: false,
          error: JSON.parse(response.data.body),
          data: null,
          code: response.data.statusCode,
        };
      }

      return {
        success: true,
        error: null,
        data: response.data,
        code: 200,
      };
    }

    return {
      success: false,
      error: null,
      data: null,
      code: 500,
    };
  } catch (e) {
    return {
      success: false,
      error: e,
      data: null,
      code: 500,
    };
  }
};

const getCommvaultData: (
  region: string,
  site_code: string
) => Promise<APIResponse> = async (region, site_code) => {
  try {
    let in_body = {
      region: region,
      site_code: site_code,
    };

    let response = await executeLambda(
      process.env.REACT_APP_COMMVAULT_DATA || "",
      in_body
    );
    console.log(response.data);
    if (response.data?.statusCode !== 200) {
      return {
        success: false,
        error: JSON.parse(response.data.body),
        data: null,
        code: response.data.statusCode,
      };
    }

    return {
      success: true,
      error: null,
      data: response.data,
      code: 200,
    };
  } catch (e) {
    return {
      success: false,
      error: e,
      data: null,
      code: 500,
    };
  }
};

/**
 * T-Shirt Sizing for Expansion Cluster
 * @param tshirt_size_value
 * @param total_vcpu
 * @param total_ram
 * @param total_storage
 * @param total_nics
 */
const getTShirtSizingForExpansionCluster: (
  tshirt_size_value: string,
  total_vcpu: string,
  total_ram: string,
  total_storage: string,
  total_nics?: string
) => Promise<APIResponse> = async (
  tshirt_size_value,
  total_vcpu,
  total_ram,
  total_storage,
  total_nics
) => {
  try {
    let in_body = {
      tshirt_size_value: tshirt_size_value,
      vCPU: total_vcpu,
      RAM: total_ram,
      STORAGE: total_storage,
    };

    let response = await executeLambda(
      process.env.REACT_APP_LAMBDA_TSHIRT_SIZING_EXPANSION_CLUSTER || "",
      in_body
    );

    if (response.success && response.data) {
      if (response.data?.statusCode !== 200) {
        return {
          success: false,
          error: JSON.parse(response.data.body),
          data: null,
          code: response.data.statusCode,
        };
      }

      return {
        success: true,
        error: null,
        data: response.data.body,
        code: 200,
      };
    }

    return {
      success: false,
      error: null,
      data: null,
      code: 500,
    };
  } catch (e) {
    return {
      success: false,
      error: e,
      data: null,
      code: 500,
    };
  }
};

/**
 * Get T-Shirt Sizes values for dropdown, based on procurement_type
 * @param procurement_type
 */
const getAllTShirtSizes: (
  procurement_type: string
) => Promise<APIResponse> = async (procurement_type) => {
  try {
    let keyConditionExpression = "PK = :pk_value";
    // let pk_value  = (procurement_type === PROCUREMENT_TYPES.NEW) ? 'NewClusterSizing' : 'ExpansionClusterSizing';
    let expressionAttributeValues = {
      ":pk_value":
        procurement_type === PROCUREMENT_TYPES.NEW
          ? "NewClusterSizing"
          : "ExpansionClusterSizing",
    };

    let response = await dbManager.queryTable(
      process.env.REACT_APP_DYNAMO_DB_SIZING || "",
      keyConditionExpression,
      undefined,
      expressionAttributeValues,
      "SK"
    );

    // let tshirt_sizes = response.data.map((data_item: { Tshirt_size: string }) => {
    //   console.debug("TESTING", data_item.Tshirt_size)
    //   if (data_item.Tshirt_size.startsWith("EXP ")) {
    //     data_item.Tshirt_size = data_item.Tshirt_size.slice("EXP ".length).slice(0, -" Gen-11".length);
    //   }
    //   // if (procurement_type === PROCUREMENT_TYPES.NEW &&  data_item.Tshirt_size.endsWith(" Gen-11")) {
    //   //     data_item.Tshirt_size =  data_item.Tshirt_size.slice(0, -" Gen-11".length);
    //   // }
    //   return data_item
    // })
    return {
      success: true,
      data: response.data,
      error: null,
      code: 200,
    };
  } catch (e: any) {
    handleError(e);
    return {
      success: false,
      code: "500",
      data: null,
      error: e,
    };
  }
};

/**
 * Get T-Shirt Size details like CPU RAM and Storage
 * @param sortKey
 */
const getSizingDetailsBySK: (sortKey: string) => Promise<APIResponse> = async (
  sortKey
) => {
  try {
    const keyConditionExpression = "PK = :pk_value AND SK = :sk_value";
    const expressionAttributeValues = {
      ":pk_value": "ExistingClusterSizing",
      ":sk_value": sortKey,
    };

    const response = await dbManager.queryTable(
      process.env.REACT_APP_DYNAMO_DB_SIZING || "",
      keyConditionExpression,
      undefined,
      expressionAttributeValues
    );

    if (response.success && response.data?.length) {
      const item = response.data[0];
      return {
        success: true,
        code: 200,
        data: {
          num_cpu_cores: item.num_cpu_cores,
          ram: item.ram,
          storage: item.storage,
        },
        error: null,
      };
    } else {
      return {
        success: false,
        code: 404,
        data: null,
        error: "No item found for given SK",
      };
    }
  } catch (e: any) {
    handleError(e);
    return {
      success: false,
      code: "500",
      data: null,
      error: e,
    };
  }
};

/**
 * Get all T-Shirt Size details for a given PK
 * @param partitionKey - defaults to "NewClusterSizing"
 */
const getSizingDetailsByPK: (
  partitionKey?: string
) => Promise<APIResponse> = async (partitionKey = "NewClusterSizing") => {
  try {
    const keyConditionExpression = "PK = :pk_value";
    const expressionAttributeValues = {
      ":pk_value": partitionKey,
    };

    const response = await dbManager.queryTable(
      process.env.REACT_APP_DYNAMO_DB_SIZING || "",
      keyConditionExpression,
      undefined,
      expressionAttributeValues
    );
    debugger

    if (response.success && response.data?.length) {
      return {
        success: true,
        code: 200,
        data: response.data,
        error: null,
      };
    } else {
      return {
        success: false,
        code: 404,
        data: null,
        error: "No items found for given PK",
      };
    }
  } catch (e: any) {
    handleError(e);
    return {
      success: false,
      code: "500",
      data: null,
      error: e,
    };
  }
};

const deleteProcurement: (PK: string) => Promise<APIResponse> = async (PK) => {
  try {
    // Get all the items related to procurement for given PK
    let keyConditionExpression = "PK = :pk_value";
    let expressionAttributeValues = {
      ":pk_value": PK,
    };
    let result_procurement_items = await dbManager.queryTable(
      process.env.REACT_APP_DYNAMO_DB_PROCUREMENTS || "",
      keyConditionExpression,
      undefined,
      expressionAttributeValues,
      "PK, SK"
    );

    if (result_procurement_items.success) {
      // Delete all items (Procurement & Stages) for given procurement
      result_procurement_items.data.map(
        async (procurement_item: any, index: number) => {
          let procurement_item_key = {
            PK: PK,
            SK: procurement_item["SK"],
          };
          let result = await dbManager.deleteObject(
            process.env.REACT_APP_DYNAMO_DB_PROCUREMENTS || "",
            procurement_item_key
          );
        }
      );
    }

    return {
      success: true,
      data: {},
      code: "",
      error: "",
    };
  } catch (e: any) {
    handleError(e);
    return {
      success: false,
      code: "500",
      data: null,
      error: e,
    };
  }
};

const getSizingResourcesValues: (
  tshirt_size_value: string
) => Promise<APIResponse> = async (tshirt_size_value) => {
  try {
    let in_body = {
      tshirt_size: tshirt_size_value,
    };

    let response = await executeLambda(
      process.env.REACT_APP_SIZING_RESOURCES_VALUES || "",
      in_body
    );

    console.log("response lambda", response);

    if (response.success && response.data) {
      // if (response.data?.statusCode !== 200) {
      //   return {
      //     success: false,
      //     error: response.data,
      //     data: null,
      //     code: response.data.statusCode
      //   }
      // }

      return {
        success: true,
        error: null,
        data: response.data,
        code: 200,
      };
    }

    return {
      success: false,
      error: null,
      data: null,
      code: 500,
    };
  } catch (e) {
    return {
      success: false,
      error: e,
      data: null,
      code: 500,
    };
  }
};

interface SwitchMapping {
  SWITCH_NAME: string;
  TotalPorts: number;
  UsedPorts: number;
}

interface SwitchMappingResponse {
  success: boolean;
  data: SwitchMapping[] | null;
  error: any;
  code: number | string;
}

/**
 * Get switch mapping details for a given cluster name
 * @param clusterName
 */
const getSwitchMappingsByClusterName = async (
  clusterName: string
): Promise<SwitchMappingResponse> => {
  try {
    // Assuming ClusterName is the partition key (PK) in the table
    const keyConditionExpression = "ClusterName = :clusterName";
    const expressionAttributeValues = {
      ":clusterName": clusterName,
    };

    const response = await dbManager.queryTable(
      process.env.REACT_APP_DYNAMO_DB_CLUSTER_SWITCH_MAPPING || "",
      keyConditionExpression,
      undefined,
      expressionAttributeValues
    );

    if (!response.success || !Array.isArray(response.data)) {
      return {
        success: false,
        data: null,
        error: "No data found or query failed",
        code: 404,
      };
    }

    return {
      success: true,
      data: response.data,
      error: null,
      code: 200,
    };
  } catch (error) {
    console.error("Error fetching switch mappings:", error);
    return {
      success: false,
      data: null,
      error,
      code: 500,
    };
  }
};

/**
 * Get switch mapping details for clusters containing a site code
 * @param siteCode - 3 letter site code to search for in cluster names
 */
const getSwitchMappingsBySiteCode = async (
  siteCode: string
): Promise<SwitchMappingResponse> => {
  try {
    const filterExpression = "contains(ClusterName, :siteCode)";
    const expressionAttributeValues = {
      ":siteCode": siteCode,
    };

    const response = await dbManager.getAllObjects(
      process.env.REACT_APP_DYNAMO_DB_CLUSTER_SWITCH_MAPPING || "",
      filterExpression,
      undefined,
      expressionAttributeValues
    );

    if (!response.success || !Array.isArray(response.data)) {
      return {
        success: false,
        data: null,
        error: "No data found or scan failed",
        code: 404,
      };
    }

    return {
      success: true,
      data: response.data,
      error: null,
      code: 200,
    };
  } catch (error) {
    console.error("Error fetching switch mappings by site code:", error);
    return {
      success: false,
      data: null,
      error,
      code: 500,
    };
  }
};

const getActionableQuote: (
  tshirt_size: string,
  CV: string,
  DL: string,
  Num1GB: number,
  Num10GB: number,
  file_name: string,
  procurement_type: string
) => Promise<APIResponse> = async (
  tshirt_size,
  CV,
  DL,
  Num1GB,
  Num10GB,
  file_name,
  procurement_type
) => {
  try {
    let in_body: any = {
      Tshirt: tshirt_size,
      CV,
      DL,
      FileName: file_name,
    };

    if (procurement_type === PROCUREMENT_TYPES.EXPANSION) {
      in_body.Num1GB = Num1GB;
      in_body.Num10GB = Num10GB;
    }

    let response = await executeLambda(
      process.env.REACT_APP_ACTIONABLE_QUOTE || "",
      in_body
    );

    if (response.success && response.data) {
      return {
        success: true,
        error: null,
        data: response.data,
        code: 200,
      };
    }
    return {
      success: false,
      error: null,
      data:  response.data,
      code: 500,
    };
  } catch (e) {
    return {
      success: false,
      error: e,
      data: null,
      code: 500,
    };
  }
};

const getSharePointFolderHierarchy: (
  folderId?: string,
  maxDepth?: number
) => Promise<APIResponse> = async (folderId = 'root', maxDepth = 2) => {
  try {
    const in_body = {
      action: 'get_folder_hierarchy',
      folder_id: folderId,
      max_depth: maxDepth
    };

    const response = await executeLambda(
      process.env.REACT_APP_SHAREPOINT_LAMBDA || "",
      in_body
    );

    if (response.success && response.data) {
      if (response.data?.statusCode !== 200) {
        const errorBody = typeof response.data.body === 'string' 
          ? JSON.parse(response.data.body) 
          : response.data.body;
        
        return {
          success: false,
          error: errorBody?.error || 'Failed to get folder hierarchy',
          data: null,
          code: response.data.statusCode,
        };
      }

      const bodyData = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body;

      return {
        success: true,
        error: null,
        data: bodyData,
        code: 200,
      };
    }

    return {
      success: false,
      error: 'No response from lambda',
      data: null,
      code: 500,
    };
  } catch (e) {
    console.error('Error in getSharePointFolderHierarchy:', e);
    return {
      success: false,
      error: e,
      data: null,
      code: 500,
    };
  }
};

const uploadFileToSharePoint: (
  folderId: string,
  fileName: string,
  fileContentBase64: string
) => Promise<APIResponse> = async (folderId, fileName, fileContentBase64) => {
  try {
    const in_body = {
      action: 'upload_file',
      folder_id: folderId,
      file_name: fileName,
      file_content: fileContentBase64
    };

    const response = await executeLambda(
      process.env.REACT_APP_SHAREPOINT_LAMBDA || "",
      in_body
    );

    if (response.success && response.data) {
      if (response.data?.statusCode !== 200) {
        return {
          success: false,
          error: JSON.parse(response.data.body),
          data: null,
          code: response.data.statusCode,
        };
      }

      return {
        success: true,
        error: null,
        data: JSON.parse(response.data.body),
        code: 200,
      };
    }

    return {
      success: false,
      error: null,
      data: null,
      code: 500,
    };
  } catch (e) {
    return {
      success: false,
      error: e,
      data: null,
      code: 500,
    };
  }
};

const createSharePointFolder: (
  parentFolderId: string,
  folderName: string
) => Promise<APIResponse> = async (parentFolderId, folderName) => {
  try {
    const in_body = {
      action: 'create_folder',
      parent_folder_id: parentFolderId || 'root',
      folder_name: folderName
    };

    const response = await executeLambda(
      process.env.REACT_APP_SHAREPOINT_LAMBDA || "",
      in_body
    );

    if (response.success && response.data) {
      if (response.data?.statusCode !== 200) {
        return {
          success: false,
          error: JSON.parse(response.data.body),
          data: null,
          code: response.data.statusCode,
        };
      }

      return {
        success: true,
        error: null,
        data: JSON.parse(response.data.body),
        code: 200,
      };
    }

    return {
      success: false,
      error: null,
      data: null,
      code: 500,
    };
  } catch (e) {
    return {
      success: false,
      error: e,
      data: null,
      code: 500,
    };
  }
};

// SharePoint functions are already implemented correctly

/**
 * Cluster name for VM Dump
 * @param cluster_name
 */
const getVMDump: (
  cluster_name?: string
) => Promise<APIResponse> = async (
  cluster_name
) => {
  try {
    let in_body = {
      cluster_name: cluster_name,
    };

    let response = await executeLambda(
      process.env.REACT_APP_VM_DUMP || "",
      in_body
    );
    
    if (response.success && response.data) {
      return {
        success: true,
        error: null,
        data: response.data,
        code: 200,
      };
    }


    return {
      success: false,
      error: null,
      data: null,
      code: 500,
    };
  } catch (e) {
    return {
      success: false,
      error: e,
      data: null,
      code: 500,
    };
  }
};

/**
 * T-Shirt Sizing for Expansion Cluster
 * @param current_size
 * @param total_vcpu
 * @param total_ram
 * @param total_storage
 */
const getTShirtSizingForRefreshCluster: (
  current_size: string,
  total_vcpu: string,
  total_ram: string,
  total_storage: string,
 
) => Promise<APIResponse> = async (
  current_size,
  total_vcpu,
  total_ram,
  total_storage,
) => {
  try {
    let in_body = {
      current_size: current_size,
      vCPU: total_vcpu,
      RAM: total_ram,
      STORAGE: total_storage,
    };

    let response = await executeLambda(
      process.env.REACT_APP_LAMBDA_TSHIRT_SIZING_REFRESH_CLUSTER || "",
      in_body
    );

    if (response.success && response.data) {
      if (response.data?.statusCode !== 200) {
        return {
          success: false,
          error: JSON.parse(response.data.body),
          data: null,
          code: response.data.statusCode,
        };
      }

      return {
        success: true,
        error: null,
        data: response.data.body,
        code: 200,
      };
    }

    return {
      success: false,
      error: null,
      data: null,
      code: 500,
    };
  } catch (e) {
    return {
      success: false,
      error: e,
      data: null,
      code: 500,
    };
  }
};




export {
  initializeProcurementApi,
  getProcurementDetails,
  getAllProcurementStages,
  updateProcurementStageItem,
  updateSizingInfoAPI,
  sendEmailAPI,
  clusterNamesAPI,
  existingClusterSizesAPI,
  getClusterInfo,
  getTShirtSizingForNewCluster,
  getTShirtSizingForExpansionCluster,
  getAllTShirtSizes,
  getSizingDetailsBySK,
  getSizingDetailsByPK,
  deleteProcurement,
  getCommvaultData,
  getSizingResourcesValues,
  getSwitchMappingsByClusterName,
  getSwitchMappingsBySiteCode,
  getActionableQuote,
  getSharePointFolderHierarchy,
  uploadFileToSharePoint,
  createSharePointFolder,
  getVMDump,
  getTShirtSizingForRefreshCluster
};